import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle, Check, ChevronDown, Clock, FileText, Filter,
  Flame, MessageCircle, Phone, Plus, RefreshCw, Search,
  Star, Trash2, TrendingUp, User, X, Zap
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;

const STATUS_CFG = {
  new:         { label: "NEW",        color: "#00d4ff", bg: "rgba(0,212,255,0.1)",  border: "rgba(0,212,255,0.25)" },
  contacted:   { label: "CONTACTED",  color: "#f59e0b", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.25)" },
  interested:  { label: "INTERESTED", color: "#a78bfa", bg: "rgba(167,139,250,0.1)",border: "rgba(167,139,250,0.25)" },
  converted:   { label: "CONVERTED",  color: "#22c55e", bg: "rgba(34,197,94,0.1)",  border: "rgba(34,197,94,0.25)" },
  lost:        { label: "LOST",       color: "#ef4444", bg: "rgba(239,68,68,0.1)",  border: "rgba(239,68,68,0.25)" },
};

const PRIORITY_CFG = {
  hot:  { label: "HOT",  color: "#ef4444", icon: Flame },
  warm: { label: "WARM", color: "#f59e0b", icon: Zap },
  cold: { label: "COLD", color: "#00d4ff", icon: Star },
};

const STATUS_TABS = [
  { key: "all",        label: "All Leads" },
  { key: "new",        label: "New" },
  { key: "contacted",  label: "Contacted" },
  { key: "interested", label: "Interested" },
  { key: "converted",  label: "Converted" },
  { key: "lost",       label: "Lost" },
];

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="lead-stat-card" data-testid={`lead-stat-${label}`}>
      <div className="lead-stat-icon" style={{ background: `${color}18`, borderColor: `${color}28` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="lead-stat-value">{value}</div>
      <div className="lead-stat-label">{label}</div>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onUpdate }) {
  const [status, setStatus] = useState(lead.status);
  const [priority, setPriority] = useState(lead.priority);
  const [noteText, setNoteText] = useState("");
  const [followUp, setFollowUp] = useState(lead.next_follow_up ? lead.next_follow_up.slice(0, 16) : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.patch(`/api/admin/leads/${lead.id}`, { status, priority, next_follow_up: followUp || null });
      toast.success("Lead updated");
      onUpdate(updated.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/leads/${lead.id}/notes`, { text: noteText.trim() });
      toast.success("Note added");
      setNoteText("");
      onUpdate({ ...lead, notes: [...(lead.notes || []), { text: noteText.trim(), by: "Admin", at: new Date().toISOString() }] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sc = STATUS_CFG[status] || STATUS_CFG.new;
  const pc = PRIORITY_CFG[priority] || PRIORITY_CFG.warm;
  const PriorityIcon = pc.icon;

  return (
    <aside className="lead-drawer" data-testid="lead-detail-drawer">
      <div className="lead-drawer-head">
        <div>
          <span className="lead-source-pill">{lead.source === "abandoned_cart" ? "Abandoned Cart" : "Manual Lead"}</span>
          <h2 data-testid="lead-drawer-name">{lead.customer_name}</h2>
          <p>{lead.customer_email || lead.customer_phone || "No contact info"}</p>
        </div>
        <button onClick={onClose} className="lead-drawer-close" data-testid="close-lead-drawer"><X size={18} /></button>
      </div>

      <div className="lead-drawer-meta">
        <div className="lead-meta-item">
          <span>Cart Value</span>
          <b style={{ color: "#22c55e" }}>{money(lead.cart_value)}</b>
        </div>
        <div className="lead-meta-item">
          <span>Items</span>
          <b>{lead.items?.length || 0}</b>
        </div>
        <div className="lead-meta-item">
          <span>Created</span>
          <b>{new Date(lead.created_at).toLocaleDateString("en-IN")}</b>
        </div>
      </div>

      {lead.items?.length > 0 && (
        <div className="lead-items-preview">
          <span className="lead-section-label">Cart Items</span>
          {lead.items.map((item, i) => (
            <div className="lead-item-row" key={i}>
              <b>{item.name}</b>
              <span>{money(item.price)} × {item.qty}</span>
            </div>
          ))}
        </div>
      )}

      <div className="lead-controls">
        <div className="lead-control-row">
          <label>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} className="lead-select" data-testid="lead-status-select">
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="lead-control-row">
          <label>Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value)} className="lead-select" data-testid="lead-priority-select">
            {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="lead-control-row">
          <label>Follow-up Date</label>
          <input type="datetime-local" value={followUp} onChange={e => setFollowUp(e.target.value)} className="lead-input" data-testid="lead-followup-input" />
        </div>
        <button className="lead-save-btn" disabled={busy} onClick={save} data-testid="save-lead-btn">
          <Check size={14} /> {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="lead-notes-section">
        <span className="lead-section-label">Notes ({lead.notes?.length || 0})</span>
        <div className="lead-notes-list" data-testid="lead-notes-list">
          {lead.notes?.length ? lead.notes.slice().reverse().map((note, i) => (
            <div className="lead-note" key={i}>
              <p>{note.text}</p>
              <small>{note.by} · {new Date(note.at).toLocaleString("en-IN")}</small>
            </div>
          )) : <p className="lead-empty-notes">No notes yet.</p>}
        </div>
        <div className="lead-add-note">
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note…" rows={2} className="lead-textarea" data-testid="lead-note-input" />
          <button onClick={addNote} disabled={busy || !noteText.trim()} className="lead-note-btn" data-testid="add-lead-note-btn">
            <MessageCircle size={13} /> Add Note
          </button>
        </div>
      </div>
    </aside>
  );
}

function AddLeadModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", cart_value: "", priority: "warm", note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/leads", { ...form, cart_value: Number(form.cart_value) || 0 });
      toast.success("Lead created");
      onAdded(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="lead-modal-overlay" data-testid="add-lead-modal">
      <div className="lead-modal">
        <div className="lead-modal-head">
          <h3>Add Manual Lead</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="lead-modal-form">
          <label>Customer Name *
            <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required placeholder="Full name" data-testid="manual-lead-name" />
          </label>
          <label>Phone
            <input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="+91 98XXX XXXXX" data-testid="manual-lead-phone" />
          </label>
          <label>Email
            <input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} placeholder="example@email.com" data-testid="manual-lead-email" />
          </label>
          <label>Estimated Value (₹)
            <input type="number" min="0" value={form.cart_value} onChange={e => setForm({ ...form, cart_value: e.target.value })} placeholder="0" data-testid="manual-lead-value" />
          </label>
          <label>Priority
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="manual-lead-priority">
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
          </label>
          <label>Initial Note
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Optional context…" rows={2} data-testid="manual-lead-note" />
          </label>
          <div className="lead-modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="lead-save-btn" disabled={busy} data-testid="submit-manual-lead">
              <Plus size={14} /> {busy ? "Adding…" : "Add Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminLeadsManager({ query = "" }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState(query);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/api/admin/leads/stats"); setStats(data); } catch (_) {}
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/leads?status=${tab === "all" ? "" : tab}&search=${encodeURIComponent(search)}`);
      setLeads(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { loadStats(); loadLeads(); }, [loadStats, loadLeads]);

  // Auto-refresh every 30s
  useEffect(() => {
    timerRef.current = setInterval(() => { loadStats(); loadLeads(); }, 30000);
    return () => clearInterval(timerRef.current);
  }, [loadStats, loadLeads]);

  const autoGenerate = async () => {
    try {
      const { data } = await api.post("/api/admin/abandoned-carts/auto-generate-leads");
      toast.success(`Auto-generated ${data.created} new leads (${data.skipped_existing} already existed)`);
      loadStats(); loadLeads();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.customer_name}?`)) return;
    try {
      await api.delete(`/api/admin/leads/${lead.id}`);
      toast.success("Lead deleted");
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      if (selectedLead?.id === lead.id) setSelectedLead(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleUpdate = (updated) => {
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
    setSelectedLead(updated);
  };

  return (
    <div className="leads-manager" data-testid="leads-manager">
      <div className="leads-top">
        <div>
          <h2>Lead Management</h2>
          <p>Abandoned cart leads + manual leads — full pipeline control</p>
        </div>
        <div className="leads-top-actions">
          <button className="lead-refresh-btn" onClick={() => { loadStats(); loadLeads(); }} data-testid="refresh-leads">
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="lead-auto-gen-btn" onClick={autoGenerate} data-testid="auto-generate-leads">
            <Zap size={14} /> Auto-Generate from Carts
          </button>
          <button className="lead-add-btn" onClick={() => setShowAdd(true)} data-testid="add-manual-lead">
            <Plus size={14} /> Add Lead
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="leads-stats-row">
        <StatCard label="Total Leads" value={stats.total || 0} color="#00d4ff" icon={User} />
        <StatCard label="Hot Leads" value={stats.hot_leads || 0} color="#ef4444" icon={Flame} />
        <StatCard label="New" value={stats.new || 0} color="#00d4ff" icon={AlertCircle} />
        <StatCard label="Contacted" value={stats.contacted || 0} color="#f59e0b" icon={Phone} />
        <StatCard label="Interested" value={stats.interested || 0} color="#a78bfa" icon={TrendingUp} />
        <StatCard label="Converted" value={stats.converted || 0} color="#22c55e" icon={Check} />
        <StatCard label="Follow-ups Today" value={stats.follow_ups_today || 0} color="#f59e0b" icon={Clock} />
      </div>

      {/* Tabs + Search */}
      <div className="leads-filters">
        <div className="leads-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.key} className={`lead-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)} data-testid={`lead-tab-${t.key}`}>
              {t.label}
              {stats[t.key] > 0 && t.key !== "all" && <span className="lead-tab-count">{stats[t.key]}</span>}
            </button>
          ))}
        </div>
        <div className="leads-search">
          <Search size={14} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, email…" data-testid="leads-search-input" />
          {search && <button onClick={() => setSearch("")}><X size={13} /></button>}
        </div>
      </div>

      <div className={`leads-body${selectedLead ? " with-drawer" : ""}`}>
        {/* Table */}
        <div className="leads-table-wrap">
          {loading ? (
            <div className="leads-loading"><RefreshCw size={18} className="spin-slow" /> Loading leads…</div>
          ) : leads.length === 0 ? (
            <div className="leads-empty" data-testid="leads-empty-state">
              <User size={36} />
              <h3>No leads yet</h3>
              <p>Generate leads from Abandoned Carts or add manually above.</p>
            </div>
          ) : (
            <table className="leads-table" data-testid="leads-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Cart Value</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Follow-up</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => {
                  const sc = STATUS_CFG[lead.status] || STATUS_CFG.new;
                  const pc = PRIORITY_CFG[lead.priority] || PRIORITY_CFG.warm;
                  const PIcon = pc.icon;
                  return (
                    <tr key={lead.id} className={selectedLead?.id === lead.id ? "active-row" : ""} data-testid={`lead-row-${lead.id}`}>
                      <td>
                        <b className="lead-row-name">{lead.customer_name}</b>
                        <small>{lead.customer_phone || lead.customer_email || "—"}</small>
                      </td>
                      <td><b style={{ color: "#22c55e" }}>{money(lead.cart_value)}</b></td>
                      <td>
                        <span className="lead-priority-badge" style={{ color: pc.color, background: `${pc.color}18`, borderColor: `${pc.color}30` }}>
                          <PIcon size={10} /> {pc.label}
                        </span>
                      </td>
                      <td>
                        <span className="lead-status-badge" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>
                          {sc.label}
                        </span>
                      </td>
                      <td><span className="lead-source-tag">{lead.source === "abandoned_cart" ? "Cart" : "Manual"}</span></td>
                      <td>
                        {lead.next_follow_up
                          ? <span className={`lead-followup-date${new Date(lead.next_follow_up) < new Date() ? " overdue" : ""}`}>
                              <Clock size={11} /> {new Date(lead.next_follow_up).toLocaleDateString("en-IN")}
                            </span>
                          : <span className="lead-no-followup">—</span>}
                      </td>
                      <td className="lead-row-actions">
                        <button onClick={() => setSelectedLead(lead)} className="lead-view-btn" data-testid={`view-lead-${lead.id}`}>
                          <FileText size={13} /> View
                        </button>
                        <button onClick={() => deleteLead(lead)} className="lead-del-btn" data-testid={`delete-lead-${lead.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selectedLead && (
          <LeadDrawer
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onUpdate={handleUpdate}
          />
        )}
      </div>

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onAdded={lead => { setLeads(prev => [lead, ...prev]); loadStats(); }} />}
    </div>
  );
}
